/**
 * RUN 97 — the Cap Milestones Watch: which players are within a few matches of their next 50th
 * cap, and which have just passed one.
 *
 * Authority: the Technical Delegate, 27 September 2026 — *"I will like to add this milestone
 * report as a standard function in our TD Control Centre (Maybe a small additional section to
 * show a few highlights and then a button to generate an updated report (dated on the report))"*
 * — and the lesson he recorded the same day, when a stale caps snapshot plus a plays-every-match
 * assumption put four wrong figures on the hand-made edition and a player's 100th cap passed
 * uncelebrated: **milestone data comes from LIVE TMS current caps, fetched at generation time,
 * never from a stored snapshot plus arithmetic.**
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE WILL NOT DO, BECAUSE IT IS WHAT WENT WRONG
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   * **It never adds matches to caps.** `Caps now` is TMS's figure exactly as the refresh read
 *     it. A player whose caps were never read from TMS (`capsReadAt` null — the bootstrap import's
 *     figure) is NOT on the watch at all; he is counted as unread, and the panel says how many.
 *   * **It never assumes a player plays.** The distance to a milestone is in the player's own
 *     APPEARANCES, and the date is only ever "likely": the Nth of his team's remaining fixtures.
 *     A player whose games played at these Games are under half his team's matches is marked
 *     `if selected`, because for him the date is the least likely of all.
 *   * **It does not pretend the caps are newer than they are.** Every team's remaining fixtures
 *     are counted from the instant ITS caps were read, and a match played since then is reported
 *     (`playedSinceRead`) so the panel can say "refresh" — rather than silently counting a match
 *     the caps do not include.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULES, ONE PLACE EACH
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   * **Milestones are every multiple of 50** — 50, 100, 150, 200, 250, 300, and on (a player at
 *     427 has a 450th coming too). {@link nextMilestone}: exactly AT a milestone, it is REACHED and
 *     the next one is the one after it.
 *   * **Within reach** is {@link WATCH_WINDOW} appearances (three) — the hand-made editions'
 *     window.
 *   * **Played at the read**: a fixture counted in the caps is one the tournament clock had
 *     finished — kick-off plus `MATCH_LENGTH_MINUTES` — by the instant its team's caps were read,
 *     through the Desk's own `matchCompleted` with no status (the status held now is today's, not
 *     the read's). A match in play at that instant may or may not be in TMS's count, so it is
 *     left in the remaining fixtures and the team is flagged `inPlayAtRead`.
 *   * **Likely date**: the Nth remaining fixture that names the team, in schedule order. Past the
 *     last one, the competition's undrawn fixtures (a placeholder where a team goes) stand in, one
 *     distinct match day per round, and the date is marked approximate — `~` on the sheet, as on
 *     the hand-made editions. A milestone beyond every fixture left is not on the watch: it will
 *     not happen at these Games.
 *   * **Reached at these Games**: the last milestone at or below his caps was passed within his
 *     appearances here (`caps − milestone < games played`). Which match it was, TMS does not say;
 *     the latest it can have been is the team's `(caps − milestone + 1)`th most recent played
 *     fixture — exactly that one if he has played every match, and `by` that date if not.
 */

import type { CapsPlayerRow, FixtureRowView } from "../../api/types.js";
import { byKickOff, matchCompleted, matchUnderWay, todayIso, addDays } from "./derive.js";

/** Every multiple of this is a milestone. */
export const MILESTONE_STEP = 50;

/** How many appearances away counts as "within reach" on the watch. */
export const WATCH_WINDOW = 3;

/** A milestone reached this many days ago or fewer is highlighted on the panel for recognition. */
export const RECENTLY_REACHED_DAYS = 3;

/** The next milestone strictly above `caps` — AT a milestone, it is the one after. */
export function nextMilestone(caps: number): number {
  return (Math.floor(caps / MILESTONE_STEP) + 1) * MILESTONE_STEP;
}

/** The last milestone at or below `caps`, or null below the first. */
export function lastMilestone(caps: number): number | null {
  const last = Math.floor(caps / MILESTONE_STEP) * MILESTONE_STEP;
  return last >= MILESTONE_STEP ? last : null;
}

/** `100th`, `150th`, `250th` — every milestone ends in zero, so `th` is always right. */
export function ordinalCap(milestone: number): string {
  return `${String(milestone)}th`;
}

export interface UpcomingMilestone {
  competitionId: string;
  teamCode: string;
  teamName: string;
  shirtNumber: number;
  displayName: string;
  capsNow: number;
  milestone: number;
  /** Appearances still needed — 1 means his next appearance is the milestone. */
  matchesAway: number;
  /** `YYYY-MM-DD`; the Nth remaining fixture's day. */
  likelyDate: string;
  /** True when the date comes from an undrawn fixture — `~` on the sheet. */
  approximate: boolean;
  /** Games played at these Games under half his team's played matches. */
  ifSelected: boolean;
  capsReadAt: string;
}

export interface ReachedMilestone {
  competitionId: string;
  teamCode: string;
  teamName: string;
  shirtNumber: number;
  displayName: string;
  capsNow: number;
  milestone: number;
  /** The latest day the milestone match can have been played. */
  reachedBy: string | null;
  /** True when he has played every match, so `reachedBy` is THE day. */
  exact: boolean;
  capsReadAt: string;
}

export interface TeamReadState {
  competitionId: string;
  teamCode: string;
  teamName: string;
  /** The latest instant this team's caps were read - its page's latest read. */
  readAt: string | null;
  /** Fixtures played since the caps were read — the caps do not include them. */
  playedSinceRead: number;
  /** A fixture of the team was under way when its caps were read. */
  inPlayAtRead: boolean;
}

export interface CapWatch {
  /** Soonest first: by likely date, then appearances away, then team and shirt. */
  upcoming: UpcomingMilestone[];
  /** Most recent first. */
  reached: ReachedMilestone[];
  /** Players whose caps have never been read from TMS — not on the watch. */
  unread: number;
  /** One entry per team with a read, for the sheet's per-team stamps and the panel's warnings. */
  teams: TeamReadState[];
}

/** The fixtures that name a team, in schedule order. */
function teamFixtures(fixtures: readonly FixtureRowView[], competitionId: string, teamCode: string): FixtureRowView[] {
  return fixtures
    .filter(
      (fixture) =>
        fixture.competitionId === competitionId &&
        (fixture.homeTeamCode === teamCode || fixture.awayTeamCode === teamCode),
    )
    .sort(byKickOff);
}

/** Played by the tournament clock alone at `at` — the read's instant, whose status we do not hold. */
function playedAt(fixture: FixtureRowView, at: Date): boolean {
  return matchCompleted(fixture, null, at);
}

interface TeamSchedule {
  /** Team fixtures finished by the read. */
  playedAtRead: FixtureRowView[];
  /** The days the team may play next, in order: named fixtures, then undrawn rounds (approximate). */
  nextDays: Array<{ date: string; approximate: boolean }>;
  playedSinceRead: number;
  inPlayAtRead: boolean;
}

function scheduleFor(
  fixtures: readonly FixtureRowView[],
  competitionId: string,
  teamCode: string,
  readAt: Date,
  now: Date,
): TeamSchedule {
  const own = teamFixtures(fixtures, competitionId, teamCode);
  const playedAtRead = own.filter((fixture) => playedAt(fixture, readAt));
  const remaining = own.filter((fixture) => !playedAt(fixture, readAt));
  const nextDays = remaining.map((fixture) => ({ date: fixture.date, approximate: false }));

  // Past the fixtures that name the team: the competition's undrawn ones, one match day a round.
  // A team plays once a day, so after its last named fixture the next round is a LATER day; with
  // none named, the read's own day still counts.
  const lastNamed = remaining.at(-1)?.date ?? null;
  const after = lastNamed ?? todayIso(readAt);
  const undrawnDays = [
    ...new Set(
      fixtures
        .filter(
          (fixture) =>
            fixture.competitionId === competitionId &&
            (fixture.homeTeamCode === null || fixture.awayTeamCode === null) &&
            !playedAt(fixture, readAt) &&
            (lastNamed === null ? fixture.date >= after : fixture.date > after),
        )
        .map((fixture) => fixture.date),
    ),
  ].sort();
  for (const date of undrawnDays) nextDays.push({ date, approximate: true });

  return {
    playedAtRead,
    nextDays,
    playedSinceRead: remaining.filter((fixture) => matchCompleted(fixture, fixture.tmsStatus, now)).length,
    inPlayAtRead: own.some((fixture) => matchUnderWay(fixture, null, readAt)),
  };
}

function bySoonest(a: UpcomingMilestone, b: UpcomingMilestone): number {
  return (
    a.likelyDate.localeCompare(b.likelyDate) ||
    a.matchesAway - b.matchesAway ||
    a.competitionId.localeCompare(b.competitionId) ||
    a.teamCode.localeCompare(b.teamCode) ||
    a.shirtNumber - b.shirtNumber
  );
}

/**
 * The watch, from the caps as read and the schedule. `window` is how many appearances away still
 * counts (the sheet's three; the panel asks for one to find the next-match milestones).
 */
export function capWatch(input: {
  players: readonly CapsPlayerRow[];
  fixtures: readonly FixtureRowView[];
  now: Date;
  window?: number;
}): CapWatch {
  const window = input.window ?? WATCH_WINDOW;
  const upcoming: UpcomingMilestone[] = [];
  const reached: ReachedMilestone[] = [];
  let unread = 0;

  const teamStates = new Map<string, TeamReadState>();
  const schedules = new Map<string, TeamSchedule>();

  for (const player of input.players) {
    if (player.caps === null || player.capsReadAt === null) {
      unread += 1;
      continue;
    }
    const readAt = new Date(player.capsReadAt);
    if (Number.isNaN(readAt.getTime())) {
      unread += 1;
      continue;
    }
    const scheduleKey = `${player.competitionId}:${player.teamCode}:${player.capsReadAt}`;
    let schedule = schedules.get(scheduleKey);
    if (schedule === undefined) {
      schedule = scheduleFor(input.fixtures, player.competitionId, player.teamCode, readAt, input.now);
      schedules.set(scheduleKey, schedule);
    }

    const teamKey = `${player.competitionId}:${player.teamCode}`;
    const state = teamStates.get(teamKey);
    if (state === undefined) {
      teamStates.set(teamKey, {
        competitionId: player.competitionId,
        teamCode: player.teamCode,
        teamName: player.teamName,
        readAt: player.capsReadAt,
        playedSinceRead: schedule.playedSinceRead,
        inPlayAtRead: schedule.inPlayAtRead,
      });
    } else if (state.readAt !== null && player.capsReadAt > state.readAt) {
      // The team's NEWEST read is its page's latest read; an older stamp on one of its rows is a
      // player that page no longer listed, whose caps were left as they were.
      state.readAt = player.capsReadAt;
      state.playedSinceRead = schedule.playedSinceRead;
      state.inPlayAtRead = schedule.inPlayAtRead;
    }

    const caps = player.caps;
    const teamPlayed = schedule.playedAtRead.length;
    const gamesPlayed = player.gamesPlayed;

    // Upcoming: the next milestone, if it is within the window AND within the fixtures left.
    const milestone = nextMilestone(caps);
    const away = milestone - caps;
    const day = schedule.nextDays[away - 1];
    if (away <= window && day !== undefined) {
      upcoming.push({
        competitionId: player.competitionId,
        teamCode: player.teamCode,
        teamName: player.teamName,
        shirtNumber: player.shirtNumber,
        displayName: player.displayName,
        capsNow: caps,
        milestone,
        matchesAway: away,
        likelyDate: day.date,
        approximate: day.approximate,
        ifSelected: gamesPlayed !== null && teamPlayed > 0 && gamesPlayed * 2 < teamPlayed,
        capsReadAt: player.capsReadAt,
      });
    }

    // Reached here: the last milestone fell within his appearances at these Games.
    const last = lastMilestone(caps);
    if (last !== null && gamesPlayed !== null && caps - last < gamesPlayed) {
      const ago = caps - last; // 0: his latest appearance was the milestone
      const latest = schedule.playedAtRead[schedule.playedAtRead.length - 1 - ago];
      reached.push({
        competitionId: player.competitionId,
        teamCode: player.teamCode,
        teamName: player.teamName,
        shirtNumber: player.shirtNumber,
        displayName: player.displayName,
        capsNow: caps,
        milestone: last,
        reachedBy: latest?.date ?? null,
        exact: latest !== undefined && gamesPlayed === teamPlayed,
        capsReadAt: player.capsReadAt,
      });
    }
  }

  return {
    upcoming: upcoming.sort(bySoonest),
    reached: reached.sort(
      (a, b) =>
        (b.reachedBy ?? "").localeCompare(a.reachedBy ?? "") ||
        a.teamCode.localeCompare(b.teamCode) ||
        a.shirtNumber - b.shirtNumber,
    ),
    unread,
    teams: [...teamStates.values()].sort(
      (a, b) => a.competitionId.localeCompare(b.competitionId) || a.teamCode.localeCompare(b.teamCode),
    ),
  };
}

/** Reached within {@link RECENTLY_REACHED_DAYS} of `today` — the panel's recognition prompt. */
export function recentlyReached(watch: CapWatch, today: string): ReachedMilestone[] {
  const since = addDays(today, -RECENTLY_REACHED_DAYS);
  return watch.reached.filter((row) => row.reachedBy !== null && row.reachedBy >= since);
}

/* ── The panel's compact default view (the TD, 27 Sep 2026, same afternoon) ────────────────
 *
 * *"This new section is far too tall and taken too much space... maybe an auto hide and only
 * show 2-3 closest milestone only by default."* The panel's first edition listed every
 * next-match milestone and every recent recognition as three-line blocks; on a heavy match day
 * that was nine entries and some 850px of rail. The default view is now {@link HIGHLIGHT_ROWS}
 * one-line rows, chosen here so the rule is tested without a DOM, and the rest folds away. */

/** How many rows the panel shows before its fold. */
export const HIGHLIGHT_ROWS = 3;

/**
 * A recognition this fresh holds a place in the default view even on a day when the next-match
 * milestones alone would fill it. Two days, not {@link RECENTLY_REACHED_DAYS}: a recognition
 * is arranged for the player's next appearance or the one after, so by day three the moment is
 * usually either arranged or gone, and the fold is the right home for it.
 */
export const FRESH_REACHED_DAYS = 2;

export type Highlight =
  | { kind: "next"; row: UpcomingMilestone }
  | { kind: "reached"; row: ReachedMilestone };

export interface MilestoneHighlights {
  /** The default view: next-match rows first (soonest first), then reached (most recent first). */
  shown: Highlight[];
  /** Next-match milestones not in `shown`, soonest first. */
  moreNext: UpcomingMilestone[];
  /** Recent recognitions not in `shown`, most recent first. */
  moreReached: ReachedMilestone[];
}

/**
 * Which rows the panel shows unfolded.
 *
 *  1. **The freshest recognition takes one reserved place**, if one was reached within
 *     {@link FRESH_REACHED_DAYS}. The TD's own direction was "if there's room", but on the days
 *     this panel matters there is never room: his screenshot had more next-match milestones than
 *     places. And the two kinds do not age alike — a next-match milestone is still on the list
 *     tomorrow if it has not happened, while a recognition not arranged in time is lost for good.
 *     Only ONE place is reserved, so the default view is still mostly what is coming.
 *  2. **Next-match milestones fill the other places**, soonest first — the "closest" he asked for.
 *  3. **Any place still empty** takes the next-most-recent recognition (fresh or not), so a quiet
 *     day shows three rows rather than one and a fold.
 *
 * `nextMatch` is expected soonest first and `reached` most recent first, the orders `capWatch`
 * and {@link recentlyReached} already return.
 */
export function milestoneHighlights(
  nextMatch: readonly UpcomingMilestone[],
  reached: readonly ReachedMilestone[],
  today: string,
): MilestoneHighlights {
  const freshSince = addDays(today, -FRESH_REACHED_DAYS);
  const freshest = reached.find((row) => row.reachedBy !== null && row.reachedBy >= freshSince);
  const reserved = freshest === undefined ? 0 : 1;

  const nextShown = nextMatch.slice(0, HIGHLIGHT_ROWS - reserved);
  const room = HIGHLIGHT_ROWS - nextShown.length;
  // `reached` is most recent first, so its head IS the freshest - the reserved place and the
  // "place still empty" rule take from the same end, and never pick the same row twice.
  const reachedShown = reached.slice(0, room);

  return {
    shown: [
      ...nextShown.map((row): Highlight => ({ kind: "next", row })),
      ...reachedShown.map((row): Highlight => ({ kind: "reached", row })),
    ],
    moreNext: nextMatch.slice(nextShown.length),
    moreReached: reached.slice(reachedShown.length),
  };
}

/** `M` / `W` — the sheet's two sections. */
export function competitionSection(competitionId: string): "Men's" | "Women's" {
  return competitionId === "1917" ? "Women's" : "Men's";
}
